import React from 'react';
import {connect} from 'react-redux';
import AudioPlayer from 'components/AudioPlayer';
import * as noNormingTutorial from './languages/no-tutorial.js';
import * as nnNormingTutorial from './languages/nn-tutorial.js';

import strings from 'utils/strings';
import {updateObject} from 'utils/reducerUtils.js';

// Tutorial content for different languages
const tutorialObj = {no: noNormingTutorial, nn: nnNormingTutorial};
let count = 1;
const maxAudioPlayCount = 1;

class Tutorial extends React.Component {
	constructor(props) {
		super(props);

		// Load tutorial data
		this.tutorial = tutorialObj[strings.getLanguage()];
		this.starterTestenSound = this.tutorial.starterTestenSound;

		this.state = {
			showTestPanel: true, // Whether to show the tutorial panel
			counter: 1, // Current assignment counter
			selectedChoice: {}, // Stores the selected choice index per assignment
			activeComment: {}, // Stores the comment to show per assignment
			activeSound: {}, // Stores the audio src per assignment
			audioKey: {}, // track number of clicks per assignment
			nextTutorial: false,
			activeRemarks: null,
			multiSelectedChoice: {},
			showChoices: false,
			audioPlayCounts: {},
		};

		// Assignments organized by counter
		this.singlePickAssignment = {
			1: this.tutorial.assignment1,
			2: this.tutorial.assignment2,
		};
		// Assignments organized by counter
		this.multiplePickAssignment = {
			3: this.tutorial.assignment3,
		};

		this.audioRefs = {};
	}

	scrollToTop = () => {
		window.scrollTo({top: 169, behavior: 'smooth'});
	};

	handleSelect = (assignmentId, choiceIndex, choice) => {
		this.setState((prev) => ({
			selectedChoice: {
				...prev.selectedChoice,
				[assignmentId]: choiceIndex,
			},
			activeComment: {
				...prev.activeComment,
				[assignmentId]: choice.comment,
			},
			activeSound: {...prev.activeSound, [assignmentId]: choice.sound},
			audioKey: {
				...prev.audioKey,
				[assignmentId]: (prev.audioKey[assignmentId] || 0) + 1, // increment
			},
			activeRemarks: choice.remarks,
		}));
	};

	updateCount() {
		this.setState({
			nextTutorial: true,
			counter: count,
			showTestPanel: false,
		});
	}

	handleMultiSelect = (assignmentId, choiceIndex) => {
		this.setState(
			(prev) => {
				// Get currently selected indices for this assignment
				const selectedIndices =
					prev.multiSelectedChoice[assignmentId] || [];
				const maxSelections = 3; // Maximum images selectable
				let updatedIndices;

				if (selectedIndices.includes(choiceIndex)) {
					// If clicked image is already selected and 3 images are already selected
					if (selectedIndices.length === maxSelections) {
						// Only keep the clicked image, deselect the rest
						updatedIndices = [choiceIndex];
					} else {
						// Otherwise, deselect the clicked image
						updatedIndices = selectedIndices.filter(
							(i) => i !== choiceIndex
						);
					}
				} else if (selectedIndices.length < maxSelections) {
					// If less than max selections, simply add the clicked image
					updatedIndices = [...selectedIndices, choiceIndex];
				} else {
					// If max selections reached, reset and select only the clicked image
					updatedIndices = [choiceIndex];
				}

				return {
					multiSelectedChoice: {
						...prev.multiSelectedChoice,
						[assignmentId]: updatedIndices, // Update state with new selection
					},
				};
			},
			() => {
				// After state update, check current selection
				const selectedIndices =
					this.state.multiSelectedChoice[assignmentId] || [];
				const assignment = this.multiplePickAssignment[assignmentId][0];

				// Get words corresponding to selected indices
				const selectedWords = selectedIndices.map(
					(idx) => assignment.choices[idx].word
				);

				// Check if all selections are made
				const allSelected =
					selectedIndices.length === assignment.correctOrder.length;

				// Helper function to check if arrays are permutations
				const isPermutation = (arr1, arr2) => {
					if (arr1.length !== arr2.length) return false;
					const sorted1 = [...arr1].sort();
					const sorted2 = [...arr2].sort();
					return sorted1.every((val, i) => val === sorted2[i]);
				};

				let comment = '';
				let sound = '';
				let activeRemarks = false;

				if (allSelected) {
					// Check if the selected words match the correct order
					const isCorrectOrder = selectedWords.every(
						(word, i) => word === assignment.correctOrder[i]
					);

					if (isCorrectOrder) {
						// Correct order selected
						comment = assignment.correctComment;
						sound = assignment.correctSound;
						activeRemarks = true;
					} else if (
						isPermutation(selectedWords, assignment.correctOrder)
					) {
						// Wrong order but correct items selected
						comment = assignment.wrongOrderComment;
						sound = assignment.wrongOrderSound;
					} else {
						// Completely wrong selection
						comment = assignment.wrongComment;
						sound = assignment.wrongSound;
					}
				}

				// Update activeComment, activeSound, audioKey and remarks
				if (comment && sound) {
					this.setState((prev) => ({
						activeComment: {
							...prev.activeComment,
							[assignmentId]: comment,
						},
						activeSound: {
							...prev.activeSound,
							[assignmentId]: sound,
						},
						audioKey: {
							...prev.audioKey,
							[assignmentId]:
								(prev.audioKey[assignmentId] || 0) + 1, // Increment key to reset AudioPlayer
						},
						activeRemarks,
					}));
				}
			}
		);
	};

	getSelectionClass = (selectedCorrect, selectedWrong) => {
		if (selectedCorrect) {
			return 'selectedCorrect';
		}
		if (selectedWrong || selectedWrong === false) {
			return 'selectedWrong';
		}
		return '';
	};
	getMultiSelectionClass = (
		selectedIndicesArray,
		multiSectedActiveRemarks
	) => {
		if (selectedIndicesArray.length <= 2) {
			return '';
		}
		if (multiSectedActiveRemarks || multiSectedActiveRemarks === true) {
			return 'selectedCorrect';
		}
		if (!multiSectedActiveRemarks || multiSectedActiveRemarks === false) {
			return 'selectedWrong';
		}
		return '';
	};

	render() {
		const {onNextStep} = this.props;
		const {activeRemarks, showChoices, audioPlayCounts} = this.state;
		this.scrollToTop();
		const isNextButtonVisible = count === 3;

		return (
			<div className="lit-test">
				{this.state.showTestPanel && count < 3 && (
					<div className="lit-sprakforstaelse lit-sprakforstaelse__tutorial">
						{this.singlePickAssignment[this.state.counter].map(
							(assignment) => {
								const {id, choices} = assignment;
								const assignmentAudioPlayCount =
									audioPlayCounts[id] || 0;
								const isAudioDisabled =
									assignmentAudioPlayCount ===
									maxAudioPlayCount;

								return (
									<div className="lit-words-wrapper" key={id}>
										{/* Main assignment word & audio */}
										<div className="lit-single-word">
											<p className="instruction">
												<AudioPlayer
													disabled={isAudioDisabled}
													inline
													autoplay
													refs={this.audioRefs}
													src={assignment.mainSound}
													onComplete={() =>
														this.setState({
															showChoices: true,
														})
													}
													onStart={() => {
														this.setState({
															audioPlayCounts:
																updateObject(
																	audioPlayCounts,
																	{
																		[id]:
																			assignmentAudioPlayCount +
																			1,
																	}
																),
														});
													}}
												/>
												<span
													className="tutorial-step-badge"
													aria-label={`Trinn ${count} av 2`}
												>
													<span className="current">
														{id}
													</span>
													<span className="sep">
														av
													</span>
													<span className="total">
														2
													</span>
												</span>
											</p>
										</div>

										{/* Choices rendered as images */}
										{showChoices && (
											<div className="lit-words">
												<div className="lit-word-list">
													{choices.map(
														(choice, i) => {
															const selected =
																this.state
																	.selectedChoice[
																	id
																] === i;
															const selectedCorrect =
																choice.remarks;
															const selectedWrong =
																choice.remarks;

															return (
																<span
																	className={`lit-word ${
																		selected
																			? 'selected'
																			: ''
																	}`}
																	key={i}
																	onClick={() => {
																		this.handleSelect(
																			id,
																			i,
																			choice
																		);
																	}}
																>
																	<img
																		className={`imgItem ${
																			selected
																				? 'selected'
																				: ''
																		} ${this.getSelectionClass(
																			selectedCorrect,
																			selectedWrong
																		)}`}
																		src={
																			choice.img
																		}
																		alt={
																			choice.word
																		}
																	/>
																</span>
															);
														}
													)}
												</div>
											</div>
										)}
										{this.state.activeComment[id] && (
											<p
												className="lit-sprakforstaelse-instruction"
												style={{display: 'none'}}
											>
												<AudioPlayer
													inline
													key={
														this.state.audioKey[id]
													}
													autoplay
													src={
														this.state.activeSound[
															id
														]
													}
													refs={this.audioRefs}
													onComplete={() => {
														if (
															this.state
																.activeRemarks
														) {
															count = count + 1;
															this.updateCount();
														}
													}}
												/>

												{/* {this.state.activeComment[id]} */}
											</p>
										)}
									</div>
								);
							}
						)}
					</div>
				)}
				{this.state.showTestPanel && count > 2 && count < 4 && (
					<div className="multi-sprakforstaelse multi-sprakforstaelse__tutorial">
						{this.multiplePickAssignment[this.state.counter].map(
							(assignment) => {
								const {id, choices} = assignment;
								const assignmentAudioPlayCount =
									audioPlayCounts[id] || 0;
								const isAudioDisabled =
									assignmentAudioPlayCount ===
									maxAudioPlayCount;
								return (
									<div
										className="multi-words-wrapper"
										key={id}
									>
										{/* Main assignment word & audio */}
										<div className="multi-single-word">
											<p className="instruction">
												<AudioPlayer
													inline
													autoplay
													refs={this.audioRefs}
													src={assignment.mainSound}
													disabled={isAudioDisabled}
													onComplete={() =>
														this.setState({
															showChoices: true,
														})
													}
													onStart={() => {
														this.setState({
															audioPlayCounts:
																updateObject(
																	audioPlayCounts,
																	{
																		[id]:
																			assignmentAudioPlayCount +
																			1,
																	}
																),
														});
													}}
												/>
												{id} av 3
											</p>
										</div>

										{/* Choices rendered as images */}
										{showChoices && (
											<div className="multi-words">
												<span className="lit-input__label lit-input__label--count">
													{id}
												</span>
												<div className="multi-word-list">
													{choices.map(
														(choice, i) => {
															const selectedIndices =
																this.state
																	.multiSelectedChoice?.[
																	id
																] || [];
															const selected =
																selectedIndices.includes(
																	i
																);

															const selectionNumber =
																selected
																	? selectedIndices.indexOf(
																			i
																	  ) + 1
																	: null;

															return (
																<span
																	className={`multi-word ${
																		selected
																			? 'selected'
																			: ''
																	}`}
																	key={i}
																	onClick={() =>
																		this.handleMultiSelect(
																			id,
																			i
																		)
																	}
																>
																	<div className="multi-img-wrapper">
																		<img
																			className={`multi-imgItem ${
																				selected
																					? 'selected'
																					: ''
																			} ${this.getMultiSelectionClass(
																				selectedIndices,
																				activeRemarks
																			)}`}
																			src={
																				choice.img
																			}
																			alt={
																				choice.word
																			}
																		/>
																		{selected && (
																			<span className="selection-circle">
																				{
																					selectionNumber
																				}
																			</span>
																		)}
																	</div>
																</span>
															);
														}
													)}
												</div>
											</div>
										)}

										{this.state.activeComment[id] && (
											<p
												className="instruction"
												style={{display: 'none'}}
											>
												<AudioPlayer
													inline
													key={
														this.state.audioKey[id]
													}
													autoplay
													src={
														this.state.activeSound[
															id
														]
													}
													refs={this.audioRefs}
													onComplete={() => {
														if (
															this.state
																.activeRemarks
														) {
															count = count + 1;
															this.setState({
																isNextButtonVisible:
																	count,
															});
														}
													}}
												/>
												{/* {this.state.activeComment[id]} */}
											</p>
										)}
									</div>
								);
							}
						)}
					</div>
				)}
				{this.state.nextTutorial && count < 3 && (
					<button
						className="lit-btn bg-primary"
						onClick={() => {
							this.setState({
								showTestPanel: true,
								nextTutorial: false,
								showChoices: false,
							});
						}}
					>
						{'Neste'}
					</button>
				)}
				{isNextButtonVisible && (
					<p className="instruction">
						<AudioPlayer
							src={this.starterTestenSound}
							inline
							autoplay
							refs={this.audioRefs}
						/>
						{strings.sprakforstaelseIntro}
					</p>
				)}
				{isNextButtonVisible && (
					<button
						className="lit-btn bg-primary"
						onClick={() => {
							onNextStep();
						}}
					>
						{strings.startTest}
					</button>
				)}
			</div>
		);
	}
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(Tutorial);
